import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Plus, Search, FileText, Trash2, Edit3, X, Check,
  FileSpreadsheet, FileEdit, Printer, Upload, Download, Copy,
} from 'lucide-react';
import api from '../utils/api';
import * as XLSX from 'xlsx';
import PageHeader from '../components/PageHeader';

// ─── 상수 ─────────────────────────────────────────────────
const COMPANY = {
  bizNum: '688-07-02108', name: '핑크페이퍼', ceo: '김태연 · 여은영',
  addr: '서울특별시 노원구 노원로22길 71 306동 1501호',
  bizType: '서비스', bizItem: '비디오물제작업 / 시각디자인업 / 공연기획업',
  tel: '010-2726-3790', bank: '하나은행(핑크페이퍼 김태연) / 244-910768-84307',
};
const BRAND = '#d4005e';
const LIGHT_PINK = '#ecd8e0';

// ─── 개인 도장 SVG ─────────────────────────────────────────

// ─── 유틸 ─────────────────────────────────────────────────
function fmt(n) { return Number(n || 0).toLocaleString('ko-KR'); }
function today() { return new Date().toISOString().slice(0, 10); }
function emptyItem() {
  return { group: '', detail: '', spec: '', price: '', qty: 1, unit: '식', count: 1, countUnit: '회', totalPrice: 0, note: '' };
}
function calcItem(item) {
  const p = Number(item.price) || 0, q = Number(item.qty) || 1, c = Number(item.count) || 1;
  return { ...item, totalPrice: p * q * c };
}
function calcTotals(items, negoAmount = 0) {
  const subtotal = items.reduce((s, r) => s + (Number(r.totalPrice) || 0), 0);
  const vat = Math.floor(subtotal * 0.1);
  const nego = Number(negoAmount) || 0;
  const total = subtotal + vat - nego;
  return { subtotal, vat, total };
}
function groupSpans(items) {
  const spanMap = {};
  let i = 0;
  while (i < items.length) {
    const g = items[i].group || '';
    let j = i + 1;
    while (j < items.length && (items[j].group || '') === g) j++;
    const info = { start: i, count: j - i, label: g };
    for (let k = i; k < j; k++) spanMap[k] = { ...info, isFirst: k === i };
    i = j;
  }
  return spanMap;
}
function fmtDate(d) {
  if (!d) return '';
  return `${d.slice(0, 4)}년 ${Number(d.slice(5, 7))}월 ${Number(d.slice(8, 10))}일`;
}

// ─── 인쇄용 HTML ──────────────────────────────────────────
function buildQuoteHtml(quote, projectName, logoUri, itemCount = 0, sealKtyUri = '', sealYeyUri = '') {
  const items = Array.isArray(quote.items) ? quote.items : [];
  const spanMap = groupSpans(items);
  const orientation = itemCount <= 10 ? 'landscape' : 'portrait';
  // @page margin:0 + body padding 방식 — 브라우저 프리뷰와 실제 인쇄 모두 동일한 여백
  const pageFullH = orientation === 'landscape' ? '210mm' : '297mm';

  const rows = items.map((row, idx) => {
    const info = spanMap[idx];
    const groupCell = info?.isFirst
      ? `<td rowspan="${info.count}" style="border-right:1px solid ${LIGHT_PINK};text-align:center;vertical-align:middle;font-size:10pt;font-weight:600;color:#444;background:#fdf5f8;padding:4px 6px;">${info.label}</td>`
      : '';
    return `<tr style="border-bottom:1px solid #f8eef3;">${groupCell}
      <td style="font-size:9pt;padding:4px 6px;">${row.detail || ''}</td>
      <td style="font-size:9pt;padding:4px;text-align:center;color:#555;">${row.spec || ''}</td>
      <td style="font-size:9pt;padding:4px 6px;text-align:right;">${fmt(row.price)}</td>
      <td style="font-size:9pt;padding:4px;text-align:center;">${row.qty}</td>
      <td style="font-size:9pt;padding:4px;text-align:center;color:#555;">${row.unit || ''}</td>
      <td style="font-size:9pt;padding:4px;text-align:center;">${row.count}</td>
      <td style="font-size:9pt;padding:4px;text-align:center;color:#555;">${row.countUnit || '회'}</td>
      <td style="font-size:10pt;padding:4px 6px;text-align:right;font-weight:600;color:#222;">${fmt(row.totalPrice)}</td>
      <td style="font-size:9pt;padding:4px;text-align:center;color:#777;">${row.note || ''}</td>
    </tr>`;
  }).join('');

  const logoHtml = logoUri
    ? `<img src="${logoUri}" style="height:34px;width:auto;object-fit:contain;" />`
    : `<div style="font-size:20pt;font-weight:900;color:${BRAND};">P!NK</div>`;

  return `<!DOCTYPE html><html lang="ko"><head><meta charset="UTF-8"/>
<style>
  @page { size: A4 ${orientation}; margin: 0; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: 'Malgun Gothic','맑은 고딕','Apple SD Gothic Neo',sans-serif;
    font-size: 9pt; color: #1a1a1a; background: #fff;
    padding: 14mm 16mm;
    min-height: ${pageFullH};
    display: flex; flex-direction: column;
  }
  @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
  table { border-collapse: collapse; }
  .spacer { flex: 1; min-height: 10pt; }
</style></head>
<body>

<!-- HEADER: 로고 좌측, QUOTATION 정중앙, 번호 우측 -->
<div style="position:relative;text-align:center;padding-bottom:8pt;border-bottom:2.5pt solid ${BRAND};margin-bottom:10pt;min-height:42pt;display:flex;align-items:center;justify-content:center;">
  <div style="position:absolute;left:0;top:50%;transform:translateY(-50%);">${logoHtml}</div>
  <div>
    <div style="font-size:22pt;font-weight:900;letter-spacing:10pt;color:#1a1a1a;">QUOTATION</div>
    ${projectName ? `<div style="font-size:8pt;color:#888;margin-top:2px;letter-spacing:1px;">[ ${projectName} ]</div>` : ''}
  </div>
  <div style="position:absolute;right:0;top:50%;transform:translateY(-50%);font-size:8pt;color:#bbb;">${quote.quoteNumber || ''}</div>
</div>

<!-- RECIPIENT + SUPPLIER -->
<div style="display:grid;grid-template-columns:1fr 1fr;gap:8pt;margin-bottom:8pt;">
  <div style="border:1px solid ${LIGHT_PINK};border-radius:5pt;padding:8pt 10pt;">
    <div style="font-size:6.5pt;color:${BRAND};font-weight:700;letter-spacing:2px;margin-bottom:5pt;">RECIPIENT · 수신처</div>
    ${[
      ['수 신 처', quote.recipientName || ''],
      ['날 &nbsp;&nbsp; 짜', fmtDate(quote.quoteDate)],
      ['유효기간', `발행 후 ${quote.validDays || 7}일`],
      ...(quote.recipientBizNum ? [['사업자번호', quote.recipientBizNum]] : []),
      ...(quote.recipientContact ? [['연 락 처', quote.recipientContact]] : []),
      ...(quote.recipientAddr ? [['주 &nbsp;&nbsp; 소', quote.recipientAddr]] : []),
    ].map(([k, v]) => `<div style="display:flex;margin-bottom:2.5pt;font-size:9pt;">
      <span style="width:58pt;font-weight:600;color:#555;flex-shrink:0;">${k}</span>
      <span>${v}</span></div>`).join('')}
  </div>
  <!-- 공급자: position:relative로 도장 오버레이 기준 -->
  <div style="position:relative;border:1px solid ${LIGHT_PINK};border-radius:5pt;padding:8pt 10pt;">
    <div style="font-size:6.5pt;color:${BRAND};font-weight:700;letter-spacing:2px;margin-bottom:5pt;">SUPPLIER · 공급자</div>
    ${[
      ['사업자번호', COMPANY.bizNum],
      ['상 &nbsp;&nbsp; 호', COMPANY.name],
      ['대 표 자', '김태연 &nbsp;·&nbsp; 여은영'],
      ['소 재 지', COMPANY.addr],
      ['업 &nbsp;&nbsp; 태', COMPANY.bizType],
      ['종 &nbsp;&nbsp; 목', COMPANY.bizItem],
      ['전 &nbsp;&nbsp; 화', COMPANY.tel],
    ].map(([k, v], i) => {
      const isRep = i === 2;
      return `<div style="position:relative;display:flex;margin-bottom:2pt;font-size:9pt;">
        ${isRep ? `
        ${sealKtyUri ? `<img src="${sealKtyUri}" style="position:absolute;left:108pt;top:-9pt;width:32pt;height:32pt;opacity:0.85;pointer-events:none;z-index:1;" />` : ''}
        ${sealYeyUri ? `<img src="${sealYeyUri}" style="position:absolute;left:142pt;top:-9pt;width:32pt;height:32pt;opacity:0.85;pointer-events:none;z-index:1;" />` : ''}` : ''}
        <span style="width:58pt;font-weight:600;color:#555;flex-shrink:0;position:relative;z-index:2;">${k}</span>
        <span style="position:relative;z-index:2;">${v}</span>
      </div>`;
    }).join('')}
  </div>
</div>

<!-- 총 견적금액 -->
<div style="background:linear-gradient(135deg,#f9eaf1,#fdf4f8);border:1px solid ${LIGHT_PINK};border-radius:5pt;padding:6pt 14pt;margin-bottom:8pt;display:flex;align-items:center;justify-content:space-between;">
  <div style="font-size:8pt;color:#888;">총 견적금액 (VAT 포함${Number(quote.negoAmount) > 0 ? ' · 네고 적용' : ''})</div>
  <div style="font-size:17pt;font-weight:900;color:${BRAND};">${fmt(quote.total)}<span style="font-size:11pt;"> 원</span></div>
</div>

<!-- 품목 테이블 -->
<table style="width:100%;table-layout:fixed;">
  <colgroup>
    <col style="width:15%"/><col/><col style="width:9%"/>
    <col style="width:11%"/><col style="width:6%"/><col style="width:6%"/>
    <col style="width:6%"/><col style="width:6%"/>
    <col style="width:12%"/><col style="width:10%"/>
  </colgroup>
  <thead>
    <tr style="background:${BRAND};color:#fff;">
      ${['항목','내역','규격','단가(원)','수량','단위','횟수','단위','산출가','비고'].map((h,i)=>`
        <th style="padding:5pt 4pt;font-size:9pt;font-weight:600;text-align:${i===1?'left':[3,8].includes(i)?'right':'center'};border-right:${i<9?'1pt solid rgba(255,255,255,0.2)':'none'};">${h}</th>`).join('')}
    </tr>
  </thead>
  <tbody>
    ${rows || `<tr><td colspan="10" style="text-align:center;padding:14pt;color:#aaa;">품목 없음</td></tr>`}
  </tbody>
</table>

<!-- 스페이서: 합계/푸터를 하단으로 밀어냄 -->
<div class="spacer"></div>

<!-- 합계 -->
<div style="display:flex;justify-content:flex-end;margin-bottom:8pt;">
  <table>
    <tr>
      <td style="padding:4pt 14pt;background:#faf0f5;font-size:9pt;color:#666;font-weight:600;border:1pt solid ${LIGHT_PINK};text-align:right;white-space:nowrap;">합 계</td>
      <td style="padding:4pt 14pt;font-size:9pt;text-align:right;border:1pt solid ${LIGHT_PINK};min-width:100pt;">${fmt(quote.subtotal)} 원</td>
    </tr>
    <tr>
      <td style="padding:4pt 14pt;background:#faf0f5;font-size:9pt;color:#666;font-weight:600;border:1pt solid ${LIGHT_PINK};text-align:right;">부가세 (10%)</td>
      <td style="padding:4pt 14pt;font-size:9pt;text-align:right;border:1pt solid ${LIGHT_PINK};">${fmt(quote.vat)} 원</td>
    </tr>
    ${Number(quote.negoAmount) > 0 ? `
    <tr>
      <td style="padding:4pt 14pt;background:#fff8f0;color:#c2410c;font-size:9pt;font-weight:600;border:1pt solid ${LIGHT_PINK};text-align:right;white-space:nowrap;">네 고 금 액</td>
      <td style="padding:4pt 14pt;font-size:9pt;text-align:right;border:1pt solid ${LIGHT_PINK};color:#c2410c;">- ${fmt(quote.negoAmount)} 원</td>
    </tr>` : ''}
    <tr>
      <td style="padding:5pt 14pt;background:${BRAND};color:#fff;font-size:10pt;font-weight:700;border:1pt solid ${LIGHT_PINK};text-align:right;white-space:nowrap;">최 종 견 적</td>
      <td style="padding:5pt 14pt;font-size:13pt;font-weight:800;text-align:right;border:1pt solid ${LIGHT_PINK};color:${BRAND};">${fmt(quote.total)} 원</td>
    </tr>
  </table>
</div>

<!-- 하단 푸터 -->
<div style="border-top:1pt solid ${LIGHT_PINK};padding-top:6pt;font-size:9pt;color:#555;line-height:1.9;">
  <div><span style="font-weight:600;display:inline-block;width:60pt;">유효기간</span>발행일로부터 ${quote.validDays || 7}일</div>
  <div><span style="font-weight:600;display:inline-block;width:60pt;">계좌번호</span>${quote.bankInfo || COMPANY.bank}</div>
  ${quote.memo ? `<div><span style="font-weight:600;display:inline-block;width:60pt;">비 고</span>${quote.memo}</div>` : ''}
</div>

</body></html>`;
}

// ─── 파트너 검색 ─────────────────────────────────────────
function PartnerSearch({ onSelect }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 260 });
  const inputRef = useRef(null);
  const dropRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (inputRef.current?.contains(e.target) || dropRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const search = useCallback(async (val) => {
    setQ(val);
    if (!val.trim()) { setResults([]); setOpen(false); return; }
    try {
      const { data } = await api.get('/partners', { params: { q: val } });
      const r = data.slice(0, 8);
      setResults(r);
      if (r.length > 0 && inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        const dropH = Math.min(r.length * 56, 224);
        const below = window.innerHeight - rect.bottom;
        setPos({
          top: below > dropH + 8 ? rect.bottom + 4 : rect.top - dropH - 4,
          left: rect.left,
          width: Math.max(rect.width, 260),
        });
        setOpen(true);
      }
    } catch {}
  }, []);

  return (
    <>
      <div className="relative flex items-center">
        <Search size={11} className="absolute left-2 text-gray-500 pointer-events-none" />
        <input ref={inputRef} value={q} onChange={e => search(e.target.value)} placeholder="파트너사 검색..."
          className="bg-gray-800 border border-gray-700 rounded-lg pl-6 pr-6 py-1 text-xs text-gray-200 placeholder-gray-600 focus:outline-none focus:border-brand/50 w-36" />
        {q && <button onClick={() => { setQ(''); setOpen(false); }} className="absolute right-1.5 text-gray-500 hover:text-gray-300"><X size={10} /></button>}
      </div>
      {open && createPortal(
        <div ref={dropRef} style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 99999 }}
          className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl max-h-56 overflow-y-auto">
          {results.map(p => (
            <button key={p.id} className="w-full text-left px-3 py-2.5 hover:bg-gray-700 text-xs border-b border-gray-700/50 last:border-0"
              onClick={() => { onSelect(p); setQ(p.name || ''); setOpen(false); }}>
              <div className="font-medium text-gray-100">{p.name}</div>
              {p.contactName && <div className="text-gray-500">{p.contactName} {p.contactPhone}</div>}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

// ─── 프로젝트 관리 모달 ──────────────────────────────────

// ─── 품목 행 ─────────────────────────────────────────────
function ItemRow({ item, idx, onChange, onRemove, isSameGroupAsPrev }) {
  function up(field, val) { onChange(idx, calcItem({ ...item, [field]: val })); }
  // focus:bg 제거 — 클릭 시 배경색 없이 border만 변경
  const base = "w-full bg-transparent border border-transparent rounded px-1.5 py-1 text-xs text-gray-100 focus:outline-none focus:border-brand/50";
  return (
    // hover: 브랜드색 극미약 틴트, group으로 모든 셀 동시 적용
    <tr className={`border-b border-gray-800/60 group ${isSameGroupAsPrev ? 'border-l-2 border-l-brand/30' : ''}`}>
      <td className="px-1 py-0.5 group-hover:bg-brand/[0.04]">
        <input value={item.group || ''} onChange={e => up('group', e.target.value)} placeholder="항목"
          className={base + ' text-center font-medium'} />
      </td>
      <td className="px-1 py-0.5 group-hover:bg-brand/[0.04]">
        <input value={item.detail || ''} onChange={e => up('detail', e.target.value)} placeholder="내역" className={base} />
      </td>
      <td className="px-1 py-0.5 group-hover:bg-brand/[0.04]">
        <input value={item.spec || ''} onChange={e => up('spec', e.target.value)} placeholder="-" className={base + ' text-center'} />
      </td>
      <td className="px-1 py-0.5 group-hover:bg-brand/[0.04]">
        <input type="number" value={item.price || ''} onChange={e => up('price', e.target.value)} placeholder="0" className={base + ' text-right'} />
      </td>
      <td className="px-1 py-0.5 group-hover:bg-brand/[0.04]">
        <input type="number" value={item.qty || 1} onChange={e => up('qty', e.target.value)} className={base + ' text-center'} />
      </td>
      <td className="px-1 py-0.5 group-hover:bg-brand/[0.04]">
        <input value={item.unit || ''} onChange={e => up('unit', e.target.value)} placeholder="식" className={base + ' text-center'} />
      </td>
      <td className="px-1 py-0.5 group-hover:bg-brand/[0.04]">
        <input type="number" value={item.count || 1} onChange={e => up('count', e.target.value)} className={base + ' text-center'} />
      </td>
      <td className="px-1 py-0.5 group-hover:bg-brand/[0.04]">
        <input value={item.countUnit || '회'} onChange={e => up('countUnit', e.target.value)} className={base + ' text-center'} />
      </td>
      <td className="px-2 py-0.5 text-right text-xs font-semibold text-brand whitespace-nowrap group-hover:bg-brand/[0.04]">
        {fmt(item.totalPrice)}
      </td>
      <td className="px-1 py-0.5 group-hover:bg-brand/[0.04]">
        <input value={item.note || ''} onChange={e => up('note', e.target.value)} placeholder="-" className={base + ' text-center'} />
      </td>
      <td className="px-1 py-0.5 text-center group-hover:bg-brand/[0.04]">
        <button onClick={() => onRemove(idx)} className="text-gray-700 hover:text-red-400 transition-colors"><X size={13} /></button>
      </td>
    </tr>
  );
}

// ─── 견적서 미리보기 (React) ──────────────────────────────
function QuotePreview({ quote, projectName, logoDataUri, sealKtyUri, sealYeyUri }) {
  const items = Array.isArray(quote.items) ? quote.items : [];
  const spanMap = groupSpans(items);

  return (
    <div style={{ fontFamily: "'Malgun Gothic','Apple SD Gothic Neo',sans-serif", background: '#fff', color: '#1a1a1a', padding: '28px 32px', borderRadius: 12, margin: '8px 4px' }}>
      {/* 헤더: 로고 좌측 절대, QUOTATION 정중앙 */}
      <div style={{ position: 'relative', textAlign: 'center', paddingBottom: 10, borderBottom: `2.5px solid ${BRAND}`, marginBottom: 14, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)' }}>
          <img src={logoDataUri || '/pinkpaper_logo.png'} style={{ height: 36, width: 'auto', objectFit: 'contain' }} alt="logo" />
        </div>
        <div>
          <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: 10, color: '#1a1a1a' }}>QUOTATION</div>
          {projectName && <div style={{ fontSize: 11, color: '#888', marginTop: 2, letterSpacing: 1 }}>[ {projectName} ]</div>}
        </div>
        <div style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: '#bbb' }}>{quote.quoteNumber}</div>
      </div>

      {/* 수신처 + 공급자 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div style={{ border: `1px solid ${LIGHT_PINK}`, borderRadius: 7, padding: '10px 14px' }}>
          <div style={{ fontSize: 8, color: BRAND, fontWeight: 700, letterSpacing: 2, marginBottom: 6 }}>RECIPIENT · 수신처</div>
          {[
            ['수 신 처', quote.recipientName],
            ['날 짜', fmtDate(quote.quoteDate)],
            ['유효기간', `발행 후 ${quote.validDays || 7}일`],
            ...(quote.recipientBizNum ? [['사업자번호', quote.recipientBizNum]] : []),
            ...(quote.recipientContact ? [['연 락 처', quote.recipientContact]] : []),
            ...(quote.recipientAddr ? [['주 소', quote.recipientAddr]] : []),
          ].filter(([, v]) => v).map(([k, v], i) => (
            <div key={i} style={{ display: 'flex', marginBottom: 3, fontSize: 11 }}>
              <span style={{ width: 64, fontWeight: 600, color: '#666', flexShrink: 0 }}>{k}</span>
              <span>{v}</span>
            </div>
          ))}
        </div>
        {/* 공급자: position:relative로 도장 오버레이 기준 */}
        <div style={{ position: 'relative', border: `1px solid ${LIGHT_PINK}`, borderRadius: 7, padding: '10px 14px' }}>
          <div style={{ fontSize: 8, color: BRAND, fontWeight: 700, letterSpacing: 2, marginBottom: 6 }}>SUPPLIER · 공급자</div>
          {[
            ['사업자번호', COMPANY.bizNum],
            ['상 호', COMPANY.name],
            ['대 표 자', '김태연 · 여은영'],
            ['소 재 지', COMPANY.addr],
            ['업 태', COMPANY.bizType],
            ['종 목', COMPANY.bizItem],
            ['전 화', COMPANY.tel],
          ].map(([k, v], i) => (
            <div key={i} style={{ position: 'relative', display: 'flex', marginBottom: 2, fontSize: 11 }}>
              {i === 2 && <>
                {sealKtyUri && <img src={sealKtyUri} style={{ position: 'absolute', left: 126, top: -10, width: 38, height: 38, opacity: 0.85, pointerEvents: 'none', zIndex: 1 }} alt="인" />}
                {sealYeyUri && <img src={sealYeyUri} style={{ position: 'absolute', left: 166, top: -10, width: 38, height: 38, opacity: 0.85, pointerEvents: 'none', zIndex: 1 }} alt="인" />}
              </>}
              <span style={{ width: 64, fontWeight: 600, color: '#666', flexShrink: 0, position: 'relative', zIndex: 2 }}>{k}</span>
              <span style={{ position: 'relative', zIndex: 2 }}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 총액 배너 */}
      <div style={{ background: 'linear-gradient(135deg,#f9eaf1,#fdf4f8)', border: `1px solid ${LIGHT_PINK}`, borderRadius: 7, padding: '7px 16px', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 11, color: '#888' }}>총 견적금액 (VAT 포함{Number(quote.negoAmount) > 0 ? ' · 네고 적용' : ''})</div>
        <div style={{ fontSize: 22, fontWeight: 900, color: BRAND }}>{fmt(quote.total)}<span style={{ fontSize: 14 }}> 원</span></div>
      </div>

      {/* 품목 테이블 */}
      <div style={{ overflowX: 'auto', marginBottom: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
          <colgroup>
            <col style={{ width: 78 }} /><col /><col style={{ width: 58 }} />
            <col style={{ width: 80 }} /><col style={{ width: 42 }} /><col style={{ width: 38 }} />
            <col style={{ width: 42 }} /><col style={{ width: 38 }} />
            <col style={{ width: 84 }} /><col style={{ width: 80 }} />
          </colgroup>
          <thead>
            <tr style={{ background: BRAND, color: '#fff' }}>
              {['항목','내역','규격','단가(원)','수량','단위','횟수','단위','산출가','비고'].map((h,i,a) => (
                <th key={h+i} style={{ padding: '6px 4px', fontSize: 11, fontWeight: 600, textAlign: i===1?'left':[3,8].includes(i)?'right':'center', borderRight: i<a.length-1?'1px solid rgba(255,255,255,0.2)':'none' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={10} style={{ textAlign: 'center', padding: 20, color: '#ccc', fontSize: 12 }}>품목 없음</td></tr>}
            {items.map((row, idx) => {
              const info = spanMap[idx];
              return (
                <tr key={idx} style={{ borderBottom: '1px solid #f8eef3' }}>
                  {info?.isFirst && (
                    <td rowSpan={info.count} style={{ textAlign: 'center', verticalAlign: 'middle', fontSize: 11, fontWeight: 600, color: '#444', background: '#fdf5f8', borderRight: `1px solid ${LIGHT_PINK}`, padding: 4 }}>
                      {info.label}
                    </td>
                  )}
                  <td style={{ fontSize: 11, padding: '5px 6px' }}>{row.detail}</td>
                  <td style={{ fontSize: 11, padding: '5px 4px', textAlign: 'center', color: '#666' }}>{row.spec}</td>
                  <td style={{ fontSize: 11, padding: '5px 6px', textAlign: 'right' }}>{fmt(row.price)}</td>
                  <td style={{ fontSize: 11, padding: '5px 4px', textAlign: 'center' }}>{row.qty}</td>
                  <td style={{ fontSize: 11, padding: '5px 4px', textAlign: 'center', color: '#666' }}>{row.unit}</td>
                  <td style={{ fontSize: 11, padding: '5px 4px', textAlign: 'center' }}>{row.count}</td>
                  <td style={{ fontSize: 11, padding: '5px 4px', textAlign: 'center', color: '#666' }}>{row.countUnit || '회'}</td>
                  <td style={{ fontSize: 12, padding: '5px 6px', textAlign: 'right', fontWeight: 600, color: '#1a1a1a' }}>{fmt(row.totalPrice)}</td>
                  <td style={{ fontSize: 10, padding: '5px 4px', textAlign: 'center', color: '#888' }}>{row.note}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 합계 */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <table style={{ borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td style={{ padding: '5px 14px', background: '#faf0f5', color: '#666', fontSize: 11, fontWeight: 600, border: `1px solid ${LIGHT_PINK}`, textAlign: 'right', whiteSpace: 'nowrap' }}>합 계</td>
              <td style={{ padding: '5px 14px', fontSize: 12, textAlign: 'right', border: `1px solid ${LIGHT_PINK}`, color: '#1a1a1a', minWidth: 120 }}>{fmt(quote.subtotal)} 원</td>
            </tr>
            <tr>
              <td style={{ padding: '5px 14px', background: '#faf0f5', color: '#666', fontSize: 11, fontWeight: 600, border: `1px solid ${LIGHT_PINK}`, textAlign: 'right', whiteSpace: 'nowrap' }}>부가세 (10%)</td>
              <td style={{ padding: '5px 14px', fontSize: 12, textAlign: 'right', border: `1px solid ${LIGHT_PINK}`, color: '#1a1a1a' }}>{fmt(quote.vat)} 원</td>
            </tr>
            {Number(quote.negoAmount) > 0 && (
              <tr>
                <td style={{ padding: '5px 14px', background: '#fff8f0', color: '#c2410c', fontSize: 11, fontWeight: 600, border: `1px solid ${LIGHT_PINK}`, textAlign: 'right', whiteSpace: 'nowrap' }}>네 고 금 액</td>
                <td style={{ padding: '5px 14px', fontSize: 12, textAlign: 'right', border: `1px solid ${LIGHT_PINK}`, color: '#c2410c' }}>- {fmt(quote.negoAmount)} 원</td>
              </tr>
            )}
            <tr>
              <td style={{ padding: '5px 14px', background: BRAND, color: '#fff', fontSize: 12, fontWeight: 700, border: `1px solid ${LIGHT_PINK}`, textAlign: 'right', whiteSpace: 'nowrap' }}>최 종 견 적</td>
              <td style={{ padding: '5px 14px', fontSize: 14, fontWeight: 800, textAlign: 'right', border: `1px solid ${LIGHT_PINK}`, color: BRAND, minWidth: 120 }}>{fmt(quote.total)} 원</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* 하단 */}
      <div style={{ borderTop: `1px solid ${LIGHT_PINK}`, paddingTop: 8, fontSize: 11, color: '#666', lineHeight: 1.9 }}>
        <div><span style={{ fontWeight: 600, display: 'inline-block', width: 64 }}>유효기간</span>발행일로부터 {quote.validDays || 7}일</div>
        <div><span style={{ fontWeight: 600, display: 'inline-block', width: 64 }}>계좌번호</span>{quote.bankInfo || COMPANY.bank}</div>
        {quote.memo && <div><span style={{ fontWeight: 600, display: 'inline-block', width: 64 }}>비 고</span>{quote.memo}</div>}
      </div>
    </div>
  );
}

// ─── 견적서 폼 ────────────────────────────────────────────
function QuoteForm({ initial, onSave, onCancel, logoDataUri, sealKtyUri, sealYeyUri, onPrint }) {
  const [form, setForm] = useState(() => ({
    quoteNumber: initial?.quoteNumber || '',
    projectId: initial?.projectId || null,
    recipientName: initial?.recipientName || '',
    recipientBizNum: initial?.recipientBizNum || '',
    recipientAddr: initial?.recipientAddr || '',
    recipientContact: initial?.recipientContact || '',
    partnerId: initial?.partnerId || null,
    quoteDate: initial?.quoteDate || today(),
    validDays: initial?.validDays ?? 7,
    items: Array.isArray(initial?.items) && initial.items.length > 0 ? initial.items : [emptyItem()],
    bankInfo: initial?.bankInfo || COMPANY.bank,
    memo: initial?.memo || '',
    status: initial?.status || 'draft',
    negoAmount: initial?.negoAmount ? String(initial.negoAmount) : '',
  }));
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    if (!initial?.quoteNumber) {
      api.get('/quotations/next-number').then(r => setForm(f => ({ ...f, quoteNumber: r.data.number }))).catch(() => {});
    }
  }, [initial?.quoteNumber]);

  function upItems(idx, updated) { setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? updated : it) })); }
  function addItem() { setForm(f => ({ ...f, items: [...f.items, emptyItem()] })); }
  function removeItem(idx) { setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) })); }
  function selectPartner(p) {
    setForm(f => ({ ...f, partnerId: p.id, recipientName: p.name || '', recipientBizNum: p.bizRegNo || '', recipientAddr: p.address || '', recipientContact: p.contactPhone || p.email || '' }));
  }

  const { subtotal, vat, total } = calcTotals(form.items, form.negoAmount);
  const inp = "w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-brand/50";
  async function submit() {
    if (!form.quoteDate) { alert('날짜를 입력해주세요.'); return; }
    setSaving(true);
    try {
      const payload = { ...form, subtotal, vat, total };
      if (initial?.id) {
        await api.put(`/quotations/${initial.id}`, payload);
      } else {
        const { data } = await api.post('/quotations', payload);
        payload.id = data.id;
      }
      onSave({ ...initial, ...payload, subtotal, vat, total });
    } catch (e) {
      alert('저장 실패: ' + (e.response?.data?.message || e.message));
    } finally { setSaving(false); }
  }

  return (
    <div className="flex flex-col h-full">
      {/* 상단: 편집/미리보기 토글만 */}
      <div className="flex-shrink-0 px-5 pt-4 pb-3 border-b border-gray-800">
        <div className="flex items-center gap-1 bg-gray-800 rounded-lg p-0.5 w-fit">
          <button onClick={() => setPreview(false)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${!preview ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-gray-300'}`}>편집</button>
          <button onClick={() => setPreview(true)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${preview ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-gray-300'}`}>미리보기</button>
        </div>
      </div>

      {/* 중간: 스크롤 콘텐츠 */}
      <div className="flex-1 overflow-y-auto">
        {preview ? (
          <QuotePreview quote={{ ...form, subtotal, vat, total }} logoDataUri={logoDataUri} sealKtyUri={sealKtyUri} sealYeyUri={sealYeyUri} />
        ) : (
          <div className="p-5 space-y-5">
            {/* 기본 정보 */}
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">견적번호</label>
                <input type="text" value={form.quoteNumber} onChange={e => setForm(f => ({ ...f, quoteNumber: e.target.value }))}
                  className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-100 focus:outline-none focus:border-brand/50 w-36" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">날짜</label>
                <input type="date" value={form.quoteDate} onChange={e => setForm(f => ({ ...f, quoteDate: e.target.value }))}
                  className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-100 focus:outline-none focus:border-brand/50 w-40" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">유효기간(일)</label>
                <input type="number" value={form.validDays} onChange={e => setForm(f => ({ ...f, validDays: Number(e.target.value) }))}
                  className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-100 focus:outline-none focus:border-brand/50 w-24" />
              </div>
            </div>

            {/* 수신처 */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-300">수신처</span>
                <PartnerSearch onSelect={selectPartner} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[['상호명','recipientName','업체명'],['사업자번호','recipientBizNum','000-00-00000'],['연락처','recipientContact','이메일 또는 전화']].map(([lbl,key,ph]) => (
                  <div key={key}>
                    <label className="block text-xs text-gray-500 mb-1">{lbl}</label>
                    <input value={form[key]||''} onChange={e => setForm(f=>({...f,[key]:e.target.value}))} placeholder={ph} className={inp} />
                  </div>
                ))}
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">주소</label>
                  <input value={form.recipientAddr||''} onChange={e => setForm(f=>({...f,recipientAddr:e.target.value}))} placeholder="주소" className={inp} />
                </div>
              </div>
            </div>

            {/* 품목 테이블 */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-300">품목</span>
                <button onClick={addItem} className="flex items-center gap-1 text-xs text-brand hover:opacity-80"><Plus size={13} /> 행 추가</button>
              </div>
              <div className="overflow-x-auto border border-gray-700 rounded-xl">
                <table style={{ minWidth: 900, width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }} className="text-xs">
                  <colgroup>
                    <col style={{ width: 116 }} />  {/* 항목 */}
                    <col />                          {/* 내역 */}
                    <col style={{ width: 60 }} />   {/* 규격 */}
                    <col style={{ width: 86 }} />   {/* 단가 */}
                    <col style={{ width: 48 }} />   {/* 수량 */}
                    <col style={{ width: 44 }} />   {/* 단위 */}
                    <col style={{ width: 48 }} />   {/* 횟수 */}
                    <col style={{ width: 44 }} />   {/* 단위 */}
                    <col style={{ width: 88 }} />   {/* 산출가 */}
                    <col style={{ width: 106 }} />  {/* 비고 */}
                    <col style={{ width: 28 }} />   {/* 삭제 */}
                  </colgroup>
                  <thead>
                    <tr className="bg-gray-800 border-b border-gray-700">
                      {[['항목','center'],['내역','left'],['규격','center'],['단가(원)','right'],
                        ['수량','center'],['단위','center'],['횟수','center'],['단위','center'],
                        ['산출가','right'],['비고','center'],['','center'],
                      ].map(([h, a], i) => (
                        <th key={i} className="py-2 px-1.5 font-semibold text-gray-500 text-[11px] whitespace-nowrap" style={{ textAlign: a }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((item, idx) => (
                      <ItemRow key={idx} item={item} idx={idx} onChange={upItems} onRemove={removeItem}
                        isSameGroupAsPrev={idx > 0 && (item.group||'') === (form.items[idx-1].group||'') && !!item.group} />
                    ))}
                    {form.items.length === 0 && (
                      <tr><td colSpan={11} className="py-6 text-center text-xs text-gray-600">품목을 추가하세요</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 합계 */}
            <div className="flex justify-end">
              <table className="text-sm border-collapse">
                <tbody>
                  <tr>
                    <td className="bg-gray-800 border border-gray-700 px-5 py-1.5 font-medium text-gray-400 text-right whitespace-nowrap">합 계</td>
                    <td className="border border-gray-700 px-5 py-1.5 text-right min-w-28 text-gray-300">{fmt(subtotal)} 원</td>
                  </tr>
                  <tr>
                    <td className="bg-gray-800 border border-gray-700 px-5 py-1.5 font-medium text-gray-400 text-right whitespace-nowrap">부가세 (10%)</td>
                    <td className="border border-gray-700 px-5 py-1.5 text-right min-w-28 text-gray-300">{fmt(vat)} 원</td>
                  </tr>
                  <tr>
                    <td className="bg-gray-800 border border-gray-700 px-5 py-1.5 font-medium text-orange-400/80 text-right whitespace-nowrap">네고 금액</td>
                    <td className="border border-gray-700 px-2 py-1 text-right min-w-28">
                      <input
                        type="number"
                        value={form.negoAmount}
                        onChange={e => setForm(f => ({ ...f, negoAmount: e.target.value }))}
                        placeholder="0 (공제 없음)"
                        className="w-full bg-gray-900 border border-orange-600/30 rounded px-2 py-0.5 text-sm text-orange-300 text-right placeholder-gray-600 focus:outline-none focus:border-orange-500/60"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="bg-gray-800 border border-gray-700 px-5 py-1.5 font-medium text-gray-400 text-right whitespace-nowrap">최종 견적</td>
                    <td className="border border-gray-700 px-5 py-1.5 text-right min-w-28 font-bold text-brand text-base">{fmt(total)} 원</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 계좌 + 비고 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">계좌번호</label>
                <input value={form.bankInfo} onChange={e => setForm(f=>({...f,bankInfo:e.target.value}))} className={inp} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">비고</label>
                <input value={form.memo} onChange={e => setForm(f=>({...f,memo:e.target.value}))} placeholder="메모" className={inp} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 하단: 저장/취소 (항상 하단 고정) */}
      <div className="flex-shrink-0 border-t border-gray-800 px-5 py-3 flex items-center justify-between">
        <div>
          {preview && (
            <button onClick={() => onPrint({ ...form, subtotal, vat, total })}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-700 text-xs text-gray-400 hover:text-white hover:border-gray-500 transition-colors">
              <Printer size={13} /> 인쇄
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onCancel} className="px-5 py-2 rounded-lg text-sm text-gray-500 hover:text-gray-300 border border-gray-800 hover:border-gray-700 transition-colors">
            취소
          </button>
          <button onClick={submit} disabled={saving}
            className="flex items-center gap-1.5 px-6 py-2 rounded-lg text-sm font-semibold text-white bg-brand hover:bg-brand/80 disabled:opacity-50 transition-colors">
            <Check size={14} />{saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── 유틸 ─────────────────────────────────────────────────
function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + 'B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'KB';
  return (bytes / 1024 / 1024).toFixed(1) + 'MB';
}

// ─── 업로드 파일 미리보기 ─────────────────────────────────
function FilePreview({ file, onDeleted, onTitleChanged }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [xlsxSheets, setXlsxSheets] = useState(null);
  const [activeSheet, setActiveSheet] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleInput, setTitleInput] = useState(file.title);
  const titleRef = useRef(null);
  const isPdf = file.mimetype?.includes('pdf');
  const isExcel = /\.(xlsx|xls)$/i.test(file.originalName) || file.mimetype?.includes('spreadsheet') || file.mimetype?.includes('excel');

  useEffect(() => {
    setBlobUrl(null); setXlsxSheets(null); setLoadError(false); setActiveSheet(0);
    api.get(`/quotation-files/${file.id}/download`, { responseType: 'blob' })
      .then(async r => {
        if (isPdf) {
          const url = URL.createObjectURL(r.data);
          setBlobUrl(url);
        } else if (isExcel) {
          const ab = await r.data.arrayBuffer();
          const wb = XLSX.read(ab, { type: 'array' });
          const sheets = wb.SheetNames.map(name => {
            const ws = wb.Sheets[name];
            const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
            return { name, rows };
          });
          setXlsxSheets(sheets);
        } else {
          // 기타: blob URL로 새 탭
          const url = URL.createObjectURL(r.data);
          setBlobUrl(url);
        }
      })
      .catch(() => setLoadError(true));
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [file.id]);

  function handleDownload() {
    api.get(`/quotation-files/${file.id}/download`, { responseType: 'blob' })
      .then(r => {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url; a.download = file.originalName; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => alert('다운로드 실패'));
  }

  function handleDelete() {
    if (!window.confirm('삭제하시겠습니까?')) return;
    api.delete(`/quotation-files/${file.id}`)
      .then(() => onDeleted(file.id))
      .catch(() => alert('삭제 실패'));
  }

  function startEditTitle() {
    setTitleInput(file.title);
    setEditingTitle(true);
    setTimeout(() => titleRef.current?.select(), 0);
  }

  function cancelEditTitle() {
    setEditingTitle(false);
    setTitleInput(file.title);
  }

  function saveTitle() {
    const t = titleInput.trim();
    if (!t) { cancelEditTitle(); return; }
    if (t === file.title) { setEditingTitle(false); return; }
    api.patch(`/quotation-files/${file.id}`, { title: t })
      .then(r => { onTitleChanged?.(r.data); setEditingTitle(false); })
      .catch(() => alert('저장 실패'));
  }

  const sheet = xlsxSheets?.[activeSheet];
  const colCount = sheet ? Math.max(...sheet.rows.map(r => r.length), 1) : 0;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* 툴바 */}
      <div className="flex items-center justify-between px-2 py-2 flex-shrink-0 border-b border-gray-800/60">
        <div className="min-w-0 mr-3 flex items-center gap-1.5 flex-1">
          {editingTitle ? (
            <>
              <input
                ref={titleRef}
                value={titleInput}
                onChange={e => setTitleInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') cancelEditTitle(); }}
                className="flex-1 min-w-0 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-sm text-white outline-none focus:border-brand"
              />
              <button onClick={saveTitle} className="text-green-400 hover:text-green-300 p-1"><Check size={14} /></button>
              <button onClick={cancelEditTitle} className="text-gray-500 hover:text-gray-300 p-1"><X size={14} /></button>
            </>
          ) : (
            <>
              <span className="text-sm font-medium text-white truncate">{file.title}</span>
              <button onClick={startEditTitle} className="text-gray-600 hover:text-gray-300 flex-shrink-0 p-0.5"><Edit3 size={12} /></button>
              <span className="text-xs text-gray-600 flex-shrink-0">{file.originalName} · {fmtSize(file.size)}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={handleDownload}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-700 text-xs text-gray-400 hover:text-white hover:border-gray-500 transition-colors">
            <Download size={13} /> 다운로드
          </button>
          <button onClick={handleDelete}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-900/50 text-xs text-red-500 hover:bg-red-900/20 transition-colors">
            <Trash2 size={13} /> 삭제
          </button>
        </div>
      </div>

      {/* 본문 */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* 로딩 */}
        {!blobUrl && !xlsxSheets && !loadError && (
          <div className="flex items-center justify-center flex-1 text-xs text-gray-600">파일 불러오는 중...</div>
        )}
        {/* 오류 */}
        {loadError && (
          <div className="flex flex-col items-center justify-center flex-1 gap-4 text-gray-500">
            <FileSpreadsheet size={48} className="opacity-20" />
            <p className="text-sm text-gray-400">미리보기를 불러올 수 없습니다.</p>
            <button onClick={handleDownload}
              className="flex items-center gap-2 px-5 py-2.5 bg-brand text-white rounded-xl hover:bg-brand/80 text-sm">
              <Download size={15} /> 파일 다운로드
            </button>
          </div>
        )}
        {/* PDF */}
        {isPdf && blobUrl && (
          <iframe src={blobUrl} className="w-full flex-1 border-0" title={file.title} />
        )}
        {/* Excel */}
        {isExcel && xlsxSheets && (
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* 시트 탭 */}
            {xlsxSheets.length > 1 && (
              <div className="flex gap-1 px-3 pt-2 border-b border-gray-800 overflow-x-auto flex-shrink-0">
                {xlsxSheets.map((s, i) => (
                  <button key={i} onClick={() => setActiveSheet(i)}
                    className={`px-3 py-1.5 text-xs rounded-t-lg whitespace-nowrap transition-colors ${activeSheet===i?'bg-gray-800 text-white':'text-gray-500 hover:text-gray-300'}`}>
                    {s.name}
                  </button>
                ))}
              </div>
            )}
            {/* 테이블 */}
            <div className="flex-1 overflow-auto p-3">
              <table className="border-collapse text-xs whitespace-nowrap">
                <tbody>
                  {sheet?.rows.map((row, ri) => (
                    <tr key={ri} className={ri === 0 ? 'bg-gray-800/60 font-semibold' : 'hover:bg-gray-800/30'}>
                      {Array.from({ length: colCount }).map((_, ci) => (
                        <td key={ci}
                          className="border border-gray-800 px-2 py-1 text-gray-200 max-w-[200px] truncate"
                          title={String(row[ci] ?? '')}>
                          {row[ci] ?? ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {/* 기타(doc 등) — blob URL로 브라우저 자체 뷰어 */}
        {!isPdf && !isExcel && blobUrl && (
          <iframe src={blobUrl} className="w-full flex-1 border-0" title={file.title} />
        )}
      </div>
    </div>
  );
}

// ─── 업로드 모달 ──────────────────────────────────────────
function UploadModal({ onClose, onUploaded }) {
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  async function handleUpload() {
    if (!title.trim() || !file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('file', file);
      const { data } = await api.post('/quotation-files', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUploaded(data);
    } catch (e) { alert('업로드 실패: ' + (e.response?.data?.message || e.message)); }
    finally { setUploading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl w-[440px]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
          <h3 className="font-semibold text-white text-sm">견적 파일 업로드</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">제목 *</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="견적서 제목"
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-brand/60" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">파일 * <span className="text-gray-600">(Excel, PDF)</span></label>
            <input type="file" accept=".xlsx,.xls,.pdf,.doc,.docx"
              onChange={e => setFile(e.target.files[0])}
              className="w-full text-sm text-gray-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-gray-700 file:text-gray-300 file:text-xs hover:file:bg-gray-600 cursor-pointer" />
            {file && <p className="text-xs text-gray-500 mt-1">{file.name} ({fmtSize(file.size)})</p>}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 transition-colors">취소</button>
            <button onClick={handleUpload} disabled={!title.trim() || !file || uploading}
              className="px-4 py-2 rounded-xl text-sm bg-brand text-white hover:bg-brand/80 disabled:opacity-40 transition-colors">
              {uploading ? '업로드 중...' : '업로드'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── 메인 ─────────────────────────────────────────────────
export default function QuotationPage() {
  const [quoteList, setQuoteList] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);   // { _type: 'quote'|'file', ...data }
  const [mode, setMode] = useState('idle');          // 'idle'|'new'|'edit'|'view'|'file'
  // 기존 견적서를 복사해 새로 만들 때의 초기값. id/견적번호가 없으므로
  // QuoteForm은 이걸 신규 작성으로 처리하고(POST) 번호는 새로 채번한다.
  const [copyFrom, setCopyFrom] = useState(null);
  // 폼 강제 리마운트용 — '새 견적서'를 연달아 누르거나 복사로 진입할 때
  // useState 초기값이 다시 계산되도록 한다.
  const [formKey, setFormKey] = useState(0);
  const [search, setSearch] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [logoDataUri, setLogoDataUri] = useState('');
  const [sealKtyUri, setSealKtyUri] = useState('');
  const [sealYeyUri, setSealYeyUri] = useState('');

  useEffect(() => {
    const toDataUri = (url, setter) =>
      fetch(url).then(r => r.blob())
        .then(blob => new Promise(resolve => { const r = new FileReader(); r.onload = () => resolve(r.result); r.readAsDataURL(blob); }))
        .then(setter).catch(() => {});
    toDataUri('/pinkpaper_logo.png', setLogoDataUri);
    toDataUri('/stamp-kty.jpeg', setSealKtyUri);
    toDataUri('/stamp-yey.png', setSealYeyUri);
  }, []);

  const loadAll = useCallback(async (autoSelect = false) => {
    setLoading(true);
    try {
      const [q, f] = await Promise.all([
        api.get('/quotations'),
        api.get('/quotation-files'),
      ]);
      setQuoteList(q.data);
      setFileList(f.data);
      if (autoSelect) {
        const mergedNow = [
          ...q.data.map(x => ({ ...x, _type: 'quote', _date: x.quoteDate || x.createdAt })),
          ...f.data.map(x => ({ ...x, _type: 'file', _date: x.createdAt })),
        ].sort((a, b) => (b._date || '').localeCompare(a._date || ''));
        if (mergedNow.length > 0) {
          const first = mergedNow[0];
          if (first._type === 'file') {
            setSelected({ ...first }); setMode('file');
          } else {
            const { data } = await api.get(`/quotations/${first.id}`);
            setSelected({ ...data, _type: 'quote' }); setMode('view');
          }
        }
      }
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(true); }, [loadAll]);

  // 두 목록을 날짜 기준 병합
  const merged = [
    ...quoteList.map(q => ({ ...q, _type: 'quote', _date: q.quoteDate || q.createdAt })),
    ...fileList.map(f => ({ ...f, _type: 'file', _date: f.createdAt })),
  ].sort((a, b) => (b._date || '').localeCompare(a._date || ''));

  const filtered = merged.filter(item => {
    if (!search) return true;
    if (item._type === 'quote') return (item.recipientName||'').includes(search) || (item.quoteNumber||'').includes(search);
    return item.title.includes(search) || (item.originalName||'').includes(search);
  });

  async function loadDetail(id) {
    try { const { data } = await api.get(`/quotations/${id}`); setSelected({ ...data, _type: 'quote' }); setMode('view'); } catch {}
  }

  async function handleQuoteDelete(id) {
    if (!window.confirm('이 견적서를 삭제하시겠습니까?')) return;
    try { await api.delete(`/quotations/${id}`); setSelected(null); setMode('idle'); loadAll(); }
    catch (e) { alert('삭제 실패: ' + (e.response?.data?.message || e.message)); }
  }

  function handleSaved(q) { setSelected({ ...q, _type: 'quote' }); setMode('view'); loadAll(); }

  // 기존 견적서를 복사해 새 견적서 작성 시작.
  // id와 견적번호는 일부러 빼서 QuoteForm이 신규(POST)로 처리하고 번호를
  // 새로 채번하게 한다. 날짜는 오늘로, 상태는 초안으로 되돌린다.
  // 필드를 통째로 펼치지 않고 명시적으로 골라 담아, 상세 응답에 딸려오는
  // 값(생성일시 등)이 새 견적서에 섞이지 않게 한다.
  function startCopy(q) {
    setCopyFrom({
      projectId:        q.projectId ?? null,
      partnerId:        q.partnerId ?? null,
      recipientName:    q.recipientName || '',
      recipientBizNum:  q.recipientBizNum || '',
      recipientAddr:    q.recipientAddr || '',
      recipientContact: q.recipientContact || '',
      validDays:        q.validDays ?? 7,
      items:            Array.isArray(q.items) ? q.items.map(it => ({ ...it })) : [],
      bankInfo:         q.bankInfo || '',
      memo:             q.memo || '',
      negoAmount:       q.negoAmount ?? null,
      quoteDate:        today(),
      status:           'draft',
    });
    setFormKey(k => k + 1);
    setMode('new');
  }

  function handleFileDeleted(id) {
    setFileList(prev => prev.filter(f => f.id !== id));
    if (selected?._type === 'file' && selected?.id === id) { setSelected(null); setMode('idle'); }
  }

  function handleFileTitleChanged(updated) {
    setFileList(prev => prev.map(f => f.id === updated.id ? { ...f, title: updated.title } : f));
    setSelected(prev => prev?.id === updated.id ? { ...prev, title: updated.title } : prev);
  }

  function handleUploaded(newFile) {
    setShowUpload(false);
    setFileList(prev => [newFile, ...prev]);
    setSelected({ ...newFile, _type: 'file' });
    setMode('file');
  }

  function handlePrint(quote) {
    const logoUri = logoDataUri || (window.location.origin + '/pinkpaper_logo.png');
    const win = window.open('', '_blank', 'width=920,height=720');
    if (!win) { alert('팝업이 차단되었습니다.'); return; }
    const items = Array.isArray(quote.items) ? quote.items : [];
    win.document.write(buildQuoteHtml(quote, null, logoUri, items.length, sealKtyUri, sealYeyUri));
    win.document.close();
    setTimeout(() => win.print(), 600);
  }

  function downloadExcel(quote) {
    const items = Array.isArray(quote.items) ? quote.items : [];
    const ws = XLSX.utils.aoa_to_sheet([
      ['견 적 서'],
      ['수 신 처', quote.recipientName||'','','','날 짜', quote.quoteDate||''],
      ['사업자번호', quote.recipientBizNum||'','','','유효기간',`발행 후 ${quote.validDays||7}일`],
      ['주 소', quote.recipientAddr||''],
      [],
      ['[공급자]','사업자번호',COMPANY.bizNum,'상호',COMPANY.name,'대표자',COMPANY.ceo],
      ['','소재지',COMPANY.addr],['','업태',COMPANY.bizType,'종목',COMPANY.bizItem],['','전화',COMPANY.tel],
      [],
      ['항목','내역','규격','단가(원)','수량','단위','횟수','단위','최종산출가','비고'],
      ...items.map(r=>[r.group,r.detail,r.spec,Number(r.price)||0,Number(r.qty)||1,r.unit,Number(r.count)||1,r.countUnit||'회',Number(r.totalPrice)||0,r.note]),
      [],
      ['합 계','','','','','','','',Number(quote.subtotal)||0,''],
      ['부가세 (10%)','','','','','','','',Number(quote.vat)||0,''],
      ['최종 견적','','','','','','','',Number(quote.total)||0,'(만원 이하 절사)'],
      ...(Number(quote.negoAmount) > 0 ? [['네고 금액','','','','','','','',Number(quote.negoAmount),'']] : []),
      [],['유효기간',`발행일로부터 ${quote.validDays||7}일`],['계좌번호',quote.bankInfo||COMPANY.bank],
    ]);
    ws['!cols']=[{wch:14},{wch:22},{wch:12},{wch:14},{wch:8},{wch:8},{wch:8},{wch:8},{wch:14},{wch:12}];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '견적서');
    XLSX.writeFile(wb, `견적서_${quote.recipientName||''}_${quote.quoteDate||''}.xlsx`);
  }

  const isSelected = (item) =>
    selected && selected._type === item._type && selected.id === item.id;

  return (
    <div className="flex flex-col h-full">
      <PageHeader icon={FileEdit} title="견적서" count={merged.length}>
        <button onClick={() => setShowUpload(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-700 rounded-lg text-xs text-gray-400 hover:text-white hover:border-gray-500 transition-colors">
          <Upload size={13} /> 파일 업로드
        </button>
        <button onClick={() => { setSelected(null); setCopyFrom(null); setFormKey(k => k + 1); setMode('new'); }}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80">
          <Plus size={14} /> 견적서 추가
        </button>
      </PageHeader>

      <div className="flex flex-1 min-h-0 gap-4 overflow-hidden">
        {/* 좌측 목록 */}
        <div className="w-64 flex-shrink-0 flex flex-col gap-3">
          <div className="flex items-center bg-gray-900 border border-gray-800 rounded-xl px-2.5">
            <Search size={13} className="text-gray-500 flex-shrink-0" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="검색..."
              className="flex-1 bg-transparent text-white text-xs py-2 px-2 focus:outline-none placeholder-gray-600" />
            {search && <button onClick={() => setSearch('')} className="text-gray-600 hover:text-gray-400"><X size={12} /></button>}
          </div>
          <div className="flex-1 overflow-y-auto flex flex-col gap-1">
            {loading && <div className="text-center py-8 text-xs text-gray-600">불러오는 중...</div>}
            {!loading && filtered.length === 0 && (
              <div className="text-center py-8 text-xs text-gray-600">{search ? '검색 결과 없음' : '견적서를 추가하세요'}</div>
            )}
            {filtered.map(item => item._type === 'file' ? (
              <button key={`f-${item.id}`}
                onClick={() => { setSelected({ ...item, _type: 'file' }); setMode('file'); }}
                className={`w-full text-left px-3 py-2.5 rounded-xl border transition-all ${isSelected(item) ? 'bg-brand/20 border-brand/30' : 'border-transparent hover:bg-gray-800'}`}>
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="text-[9px] bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded font-medium flex-shrink-0">파일</span>
                  <span className="text-xs font-medium text-white truncate">{item.title}</span>
                </div>
                <div className="text-[10px] text-gray-500">{item.createdAt?.slice(0, 10)} · {fmtSize(item.size)}</div>
              </button>
            ) : (
              <button key={`q-${item.id}`}
                onClick={() => loadDetail(item.id)}
                className={`w-full text-left px-3 py-2.5 rounded-xl border transition-all ${isSelected(item) ? 'bg-brand/20 border-brand/30' : 'border-transparent hover:bg-gray-800'}`}>
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-xs font-medium text-white truncate">{item.recipientName || '수신처 미입력'}</span>
                </div>
                <div className="text-[10px] text-gray-500">{item.quoteDate}{item.authorName ? ` · ${item.authorName}` : ''}</div>
                <div className="text-sm font-semibold text-brand mt-0.5">{fmt(item.total)}원</div>
              </button>
            ))}
          </div>
        </div>

        {/* 우측 패널 */}
        <div className="flex-1 min-w-0 overflow-hidden">
          {mode === 'idle' && (
            <div className="flex flex-col items-center justify-center h-full text-gray-600 gap-3">
              <FileText size={40} className="opacity-30" />
              <p className="text-sm">견적서를 선택하거나 새로 작성하세요.</p>
            </div>
          )}
          {mode === 'file' && selected?._type === 'file' && (
            <FilePreview file={selected} onDeleted={handleFileDeleted} onTitleChanged={handleFileTitleChanged} />
          )}
          {(mode === 'new' || mode === 'edit') && (
            <QuoteForm key={mode === 'edit' ? `e${selected?.id}` : `n${formKey}`}
              initial={mode==='edit' ? selected : copyFrom}
              onSave={handleSaved} onCancel={() => setMode(selected?._type === 'quote' ? 'view' : 'idle')}
              logoDataUri={logoDataUri} sealKtyUri={sealKtyUri} sealYeyUri={sealYeyUri} onPrint={handlePrint} />
          )}
          {mode === 'view' && selected?._type === 'quote' && (
            <div className="flex flex-col h-full overflow-hidden">
              <div className="flex items-center justify-between px-2 py-2 flex-shrink-0 border-b border-gray-800/60">
                <span className="text-xs text-gray-600">{selected.quoteNumber}{selected.authorName ? ` · 작성 ${selected.authorName}` : ''}</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => downloadExcel(selected)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-700 text-xs text-gray-400 hover:text-white hover:border-gray-500 transition-colors">
                    <FileSpreadsheet size={13} /> Excel
                  </button>
                  <button onClick={() => handlePrint(selected)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-700 text-xs text-gray-400 hover:text-white hover:border-gray-500 transition-colors">
                    <Printer size={13} /> 인쇄
                  </button>
                  <button onClick={() => startCopy(selected)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-700 text-xs text-gray-400 hover:text-white hover:border-gray-500 transition-colors">
                    <Copy size={13} /> 복사
                  </button>
                  <button onClick={() => setMode('edit')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-700 text-xs text-gray-400 hover:text-white hover:border-gray-500 transition-colors">
                    <Edit3 size={13} /> 수정
                  </button>
                  <button onClick={() => handleQuoteDelete(selected.id)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-900/50 text-xs text-red-500 hover:bg-red-900/20 transition-colors">
                    <Trash2 size={13} /> 삭제
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                <QuotePreview quote={selected} logoDataUri={logoDataUri} sealKtyUri={sealKtyUri} sealYeyUri={sealYeyUri} />
              </div>
            </div>
          )}
        </div>
      </div>

      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onUploaded={handleUploaded} />}
    </div>
  );
}
